import React, { useState } from 'react';
import MainLayout from '../../layouts/MainLayout.jsx';
import { AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  ListFilter,
  CheckCircle2,
  Mail
} from 'lucide-react';
import BannerCarousel from '../../components/home/BannerCarousel.jsx';
import FeaturedProductCard from '../../components/common/FeaturedProductCard.jsx';
import ProductSkeleton from '../../components/common/ProductSkeleton.jsx';
import PremiumSection from '../../components/home/PremiumSection.jsx';
import ExclusiveProductsSection from '../../components/home/ExclusiveProductsSection.jsx';
import FeaturedProductsSection from '../../components/home/FeaturedProductsSection.jsx';
import BrandsSection from '../../components/home/BrandsSection.jsx';
import Seo from '../../components/Seo/Seo.jsx';

const Home = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('All Products');

  // Fetch home config
  const { data: homeConfig = {} } = useQuery({
    queryKey: ['homeConfig'],
    queryFn: () => fetch(`/api/settings/home?v=${Date.now()}`).then(res => res.json())
  });

  const { data: banners = [], isLoading: isBannerLoading } = useQuery({
    queryKey: ['banners'],
    queryFn: () => fetch(`/api/banners`).then(res => res.json())
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => fetch(`/api/categories`).then(res => res.json())
  });

  const { data: brands = [] } = useQuery({
    queryKey: ['brands'],
    queryFn: () => fetch(`/api/brands`).then(res => res.json())
  });

  const { data: productResponse = { data: [], count: 0 }, isLoading: isProdLoading } = useQuery({
    queryKey: ['products-home'],
    staleTime: 0,
    queryFn: async () => {
      const r1 = await fetch(`/api/products/search?homeshow=yes&limit=500`).then(res => res.json());
      if ((r1?.data?.length || 0) > 0) return r1;
      // No products flagged homeshow=yes — fall back to latest active products
      return fetch(`/api/products/search?limit=500`).then(res => res.json());
    }
  });

  const { data: exclusiveProductsResponse = { data: [] } } = useQuery({
    queryKey: ['products-exclusive'],
    queryFn: () => fetch(`/api/products/search?exclusive=yes&limit=100`).then(res => res.json())
  });

  const { data: featuredProductsResponse = { data: [] } } = useQuery({
    queryKey: ['products-featured'],
    queryFn: () => fetch(`/api/products/search?featured=yes&limit=100`).then(res => res.json())
  });

  const featuredProducts = featuredProductsResponse.data || [];
  const exclusiveProducts = exclusiveProductsResponse.data || [];
  const premiumCategories = categories.filter(cat => cat.home_show === 'yes');
  const products = productResponse.data || [];

  const bannerConfig = homeConfig.bannerConfig || { scrollInterval: 3, autoPlay: true };
  const bannerStats = homeConfig.bannerStats || [];
  const globalReviews = homeConfig.globalPartnerReviews || {};
  const hero = homeConfig.hero || {};
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleJoinList = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      const res = await fetch(`/api/earlyaccess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (res.ok) {
        alert('Successfully joined the early access list!');
        setEmail('');
      } else {
        alert('Failed to join the list. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = ['All Products'];

  const exclusiveIds = new Set(exclusiveProducts.map(p => p.p_id || p.id));
  const featuredIds = new Set(featuredProducts.map(p => p.p_id || p.id));
  const baseProducts = activeTab === 'All Products'
    ? products
    : products.filter(p => p.category_name === activeTab || p.category === activeTab);

  // Within a bucket, keep the existing behaviour: plain products first, then
  // exclusive, then featured (each pushed toward the back of its own bucket).
  const orderWithinBucket = (list) => [
    ...list.filter(p => !exclusiveIds.has(p.p_id || p.id) && !featuredIds.has(p.p_id || p.id)),
    ...list.filter(p => exclusiveIds.has(p.p_id || p.id)),
    ...list.filter(p => featuredIds.has(p.p_id || p.id)),
  ];

  // Category priority: Baby Milk Formula first, Electronics Accessories
  // (Fast Moving) pushed last, everything else in between.
  const BABY_MILK_C_ID = 'ce306609-08b8-4373-b416-39b75cc71758';
  const ELECTRONICS_ACCESSORIES_C_ID = 'a0bdb5c3-f6dd-4636-9775-7a1d8ab15bb2';
  const babyMilkBucket = baseProducts.filter(p => p.c_id === BABY_MILK_C_ID);
  const electronicsBucket = baseProducts.filter(p => p.c_id === ELECTRONICS_ACCESSORIES_C_ID);
  const middleBucket = baseProducts.filter(p => p.c_id !== BABY_MILK_C_ID && p.c_id !== ELECTRONICS_ACCESSORIES_C_ID);

  const filteredProducts = [
    ...orderWithinBucket(babyMilkBucket),
    ...orderWithinBucket(middleBucket),
    ...orderWithinBucket(electronicsBucket),
  ];

  const getImageUrl = (url) => {
    if (!url) return "https://images.unsplash.com/photo-1584017911766-d451b3d0e843?w=800&q=80";
    if (url.startsWith('http')) return url;
    if (url.startsWith('/')) return url;
    return `/${url}`;
  };

  return (
    <MainLayout>
      <Seo
        path="/"
        title="Premium Supplements, Wellness & Wholesale Products"
        description="Create Enterprise — trusted supplier of premium supplements, wellness products, wholesale FMCG and private-label manufacturing. Fast worldwide shipping."
      />
      <div className="pb-4 space-y-16 sm:space-y-20">

        {/* 1. Banner Carousel */}
        <BannerCarousel
          banners={banners}
          bannerStats={bannerStats}
          scrollInterval={bannerConfig.scrollInterval}
          autoPlay={bannerConfig.autoPlay}
          isLoading={isBannerLoading}
        />

        {/* 1.05 Brands, Premium Categories, Exclusive Offers, Featured Products & Tabs */}
        <div className="space-y-4 !mt-4 sm:!mt-6">
          <PremiumSection categories={premiumCategories} />
          <ExclusiveProductsSection products={exclusiveProducts} title={hero.exclusiveTitle} />
          <FeaturedProductsSection products={featuredProducts} title={hero.featuredTitle} />

          {/* 2. Product Tabs & Grid Section */}
          <section className="w-full px-2 sm:px-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 sm:gap-6 border-b border-slate-100 pb-4 mb-6 sm:mb-10">
              <div className="flex items-center gap-5 sm:gap-8 overflow-x-auto no-scrollbar pb-2 md:pb-0 -mx-3 px-3 sm:mx-0 sm:px-0">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`text-sm font-black whitespace-nowrap uppercase tracking-widest relative pb-4 transition-colors ${activeTab === tab ? 'text-slate-900 border-b-2 border-[#FF6B35]' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <button
                onClick={() => navigate('/products')}
                className="flex w-full md:w-auto items-center justify-center gap-2 px-6 py-2.5 rounded-full border border-slate-200 text-slate-600 font-black text-[10px] uppercase tracking-[0.2em] hover:border-slate-900 hover:text-slate-900 transition-all active:scale-95 whitespace-nowrap"
              >
                <ListFilter size={16} />
                Filter & Sort
              </button>
            </div>

            {filteredProducts.length === 0 && !isProdLoading ? (
              <div className="py-12 sm:py-20 text-center bg-slate-50 rounded-[2rem] sm:rounded-[3rem] border border-dashed border-slate-200 mb-8">
                <p className="text-slate-400 font-black uppercase tracking-widest text-xs">No products found in this category</p>
              </div>
            ) : (
              <div className="grid pb-8 [grid-template-columns:repeat(auto-fill,minmax(160px,1fr))] lg:[grid-template-columns:repeat(auto-fill,minmax(168px,1fr))]" style={{gap:'10px'}}>
                {isProdLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <div key={`skeleton-${i}`} className="h-[340px] overflow-hidden">
                      <ProductSkeleton index={i} />
                    </div>
                  ))
                ) : (
                  filteredProducts.map((product, i) => (
                    <div key={product.id || product.p_id}>
                      <FeaturedProductCard product={product} index={i} />
                    </div>
                  ))
                )}
              </div>
            )}
          </section>
          <BrandsSection brands={brands.filter(b => b.home_show === 'yes')} />
        </div>

        {/* 5. Early Access Newsletter Section */}
        <section className="w-full px-2 sm:px-4">
          <div className="rounded-[1.5rem] sm:rounded-[2rem] bg-gradient-to-r from-orange-600 to-orange-700 px-6 sm:px-10 py-7 sm:py-9 shadow-lg shadow-orange-600/20">
            <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-10">
              {/* Left: icon + heading */}
              <div className="flex items-center gap-4 flex-1 w-full">
                <div className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 border-white/40 flex items-center justify-center">
                  <Mail size={22} className="text-white" />
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-white leading-snug max-w-md">
                  Get the best health tips &amp; exclusive offers straight to your inbox.
                </h3>
              </div>

              {/* Right: form */}
              <div className="w-full lg:w-auto lg:min-w-[440px]">
                <form onSubmit={handleJoinList} className="flex gap-2 bg-white rounded-xl p-1.5 shadow-md">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                    className="flex-1 min-w-0 px-3 bg-transparent text-sm text-slate-900 font-medium outline-none placeholder:text-slate-400"
                  />
                  <button
                    disabled={isSubmitting}
                    type="submit"
                    className="px-5 sm:px-7 py-2.5 bg-orange-600 text-white rounded-lg font-bold text-sm hover:bg-orange-700 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? '...' : 'Subscribe'}
                  </button>
                </form>
                <p className="text-white/70 text-[11px] sm:text-xs mt-2 lg:pl-1">No spam, unsubscribe anytime.</p>
              </div>
            </div>
          </div>
        </section>

      </div>
    </MainLayout>
  );
};

export default Home;
